/**
 * @fileoverview A program's substitute requests (docs/substitute-requests.md):
 * use the organization's default, or set this program's own.
 *
 * Stored on the program as `substitute_policy` — null inherits the org default,
 * an object replaces it whole — through the same all-optional
 * `PUT /org/programs/:id` diff the listing uses. Each change saves at once and
 * rolls back if it fails.
 *
 * A private-lessons program has no class sessions to cover, so the card says
 * so rather than offering a switch that could do nothing.
 *
 * Custom starts from the organization's default, so it waits for that read.
 * A read that fails says so and offers it again: the server's built-in
 * default (off) shown or saved in its place would switch requests off for a
 * program whose organization has them on.
 */

import { useEffect, useState } from "react";
import { toast } from "sonner";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader, TextTabs } from "@/components/studio/primitives";
import SubstitutePolicyFields from "@/components/org/SubstitutePolicyFields";
import { DEFAULT_SUBSTITUTE_POLICY, policySummary } from "@/lib/substitutes";

export default function ProgramSubstituteSettings({ program, onSaved }) {
  const [override, setOverride] = useState(program.substitute_policy ?? null);
  const [orgDefault, setOrgDefault] = useState(null);
  const [defaultFailed, setDefaultFailed] = useState(false);
  const [defaultReads, setDefaultReads] = useState(0);
  const [saving, setSaving] = useState(false);
  const classBased = (program.program_type || "private_lessons") !== "private_lessons";

  useEffect(() => setOverride(program.substitute_policy ?? null), [program.substitute_policy]);

  useEffect(() => {
    let live = true;
    orgApi
      .get("/org/substitute-policy")
      .then((res) => live && setOrgDefault(res.data?.policy || res.data || DEFAULT_SUBSTITUTE_POLICY))
      .catch(() => live && setDefaultFailed(true));
    return () => {
      live = false;
    };
  }, [defaultReads]);

  const readDefaultAgain = () => {
    setDefaultFailed(false);
    setDefaultReads((n) => n + 1);
  };

  const save = async (next) => {
    const prev = override;
    setOverride(next);
    setSaving(true);
    try {
      const res = await orgApi.put(`/org/programs/${program.program_id}`, { substitute_policy: next });
      onSaved?.(res.data?.program || res.data);
    } catch (err) {
      setOverride(prev);
      toast.error(getOrgErrorMessage(err, "Couldn't save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="mb-6" padding={0} data-testid="program-substitute-settings">
      <div className="px-5 pt-5 md:px-6">
        <SectionHeader className="mb-2">Substitute requests</SectionHeader>
        {!classBased && (
          <p className="pb-5 font-sans text-[13px] text-[var(--text-tertiary)]" data-testid="program-sub-private">
            Families book this program directly, so there are no class sessions to request cover for.
          </p>
        )}
      </div>
      {classBased && (
        <>
          <div className="px-5 pb-4 md:px-6">
            <TextTabs
              testId="program-sub-mode"
              value={override ? "custom" : "default"}
              onChange={(id) => {
                if (id === "default" && override) save(null);
                if (id === "custom" && !override && orgDefault) save({ ...DEFAULT_SUBSTITUTE_POLICY, ...orgDefault });
              }}
              options={[
                { id: "default", label: "Organization default", testId: "program-sub-default" },
                {
                  id: "custom",
                  label: "Custom for this program",
                  testId: "program-sub-custom",
                  disabled: !override && !orgDefault,
                },
              ]}
            />
          </div>
          {override ? (
            <SubstitutePolicyFields value={override} onChange={save} disabled={saving} scope="program" testId="program-sub-policy" />
          ) : (
            <p className="px-5 pb-5 font-sans text-[14px] text-[var(--text-secondary)] md:px-6" data-testid="program-sub-inherited">
              {orgDefault ? (
                policySummary(orgDefault)
              ) : defaultFailed ? (
                <>
                  Couldn&rsquo;t load the organization default.{" "}
                  <button
                    type="button"
                    onClick={readDefaultAgain}
                    data-testid="program-sub-default-retry"
                    className="font-medium text-[var(--accent-yellow-text)] hover:underline"
                  >
                    Try again
                  </button>
                </>
              ) : (
                "Loading…"
              )}
              <span className="mt-1 block font-sans text-[13px] text-[var(--text-tertiary)]">
                Set in Settings → Features. Choose Custom to set this program&rsquo;s own.
              </span>
            </p>
          )}
        </>
      )}
    </Card>
  );
}
